import { supabase } from "./supabaseClient";

const USER_KEY = "existingUser";

const registrationService = {
  // Helper with timeout
  async withTimeout(promise, ms = 4000) {
    return Promise.race([
      promise,
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Supabase auth timeout")), ms)
      ),
    ]);
  },

  // async getCurrentUser() {
  //   try {
  //     // ✅ 1. Check localStorage first
  //     const token = localStorage.getItem("sol-sb-token");

  //     if (token) {
  //       // Use the token to fetch user from Supabase
  //       const { data, error } = await supabase.auth.getUser(token);
  //       if (error) console.error(error);
  //       if (data?.user) return data.user;
  //     }

  //     const existingUser = localStorage.getItem("existingUser");
  //     if (existingUser) {
  //       return JSON.parse(existingUser);
  //     }

  //     // ✅ 2. Fallback to Supabase session
  //     const { data: sessionData, error: sessionError } =
  //       await supabase.auth.getSession();
  //     console.log("Session:", sessionData, sessionError);

  //     const { data, error } = await supabase.auth.getUser();
  //     const user = data?.user || null;

  //     if (error) throw error;
  //     if (user) return user;

  //     return null;
  //   } catch (err) {
  //     console.error("Error in getCurrentUser:", err);
  //     return null;
  //   }
  // },

  async getCurrentUser() {
    try {
      // 1. Check cache first (fastest)
      const cached = localStorage.getItem(USER_KEY);
      if (cached) {
        return JSON.parse(cached);
      }

      // 2. Ask Supabase for session (safe with timeout)
      const { data, error } = await this.withTimeout(
        supabase.auth.getSession()
      );

      if (error) {
        console.error("getSession error:", error.message);
        return null;
      }

      if (data?.session?.user) {
        localStorage.setItem(USER_KEY, JSON.stringify(data.session.user));
        return data.session.user;
      }

      return null;
    } catch (err) {
      console.error("Error in getCurrentUser:", err);
      return null;
    }
  },

  // Enhanced save registration that works seamlessly for both authenticated and guest users
  async saveRegistration(formData) {
    try {
      const user = await this.getCurrentUser();

      // Enhanced guest user flow - always save to localStorage first
      // if (!user) {
      try {
        const guestData = {
          ...formData,
          lastSaved: new Date()?.toISOString(),
          isGuest: true,
          guestId: `guest_${Date.now()}_${Math.random()
            ?.toString(36)
            ?.substr(2, 9)}`,
        };

        localStorage.setItem(
          "guestRegistrationData",
          JSON.stringify(guestData)
        );
      } catch (error) {
        console.log("Failed to save registration data locally : ", error);
      }
      // }

      let existingProfile = null;

      if (user?.id) {
        // Authenticated user flow - FIXED: Check for existing registration first
        // First check if user has a profile (required for registrations table FK constraint)
        const { data, error: profileError } = await supabase
          .from("user_profiles")
          .select("id")
          .eq("id", user?.id)
          .maybeSingle(); // safer than .single()

        if (profileError && profileError?.code !== "PGRST116") {
          return {
            success: false,
            error: "Failed to verify user profile: " + profileError?.message,
          };
        }

        existingProfile = data;
      }

      // Create user profile if it doesn't exist (handle race condition with trigger)
      if (!existingProfile) {
        const { error: createProfileError } = await supabase
          ?.from("user_profiles")
          ?.insert({
            id: user?.id,
            email: user?.email,
            full_name:
              formData?.fullName ||
              user?.user_metadata?.full_name ||
              user?.email?.split("@")?.[0],
          });

        if (
          createProfileError &&
          !createProfileError?.message?.includes("duplicate key")
        ) {
          return {
            success: false,
            error:
              "Failed to create user profile: " + createProfileError?.message,
          };
        }
      }

      // Check if registration already exists
      const { data: existingRegistration } = await supabase
        ?.from("registrations")
        ?.select("id")
        ?.eq("user_id", user?.id)
        ?.single();

      // Prepare registration data for database
      const registrationData = {
        user_id: user?.id,
        selected_profile: formData?.selectedProfile,
        full_name: formData?.fullName,
        date_of_birth: formData?.dateOfBirth || null,
        gender: formData?.gender || null,
        email: formData?.email,
        mobile_number: formData?.mobileNumber,
        whatsapp_number: formData?.whatsappNumber || null,
        aadhar_number: formData?.aadharNumber || null,
        father_name: formData?.fatherName || null,
        mother_name: formData?.motherName || null,
        address: formData?.address || null,
        city: formData?.city || null,
        state: formData?.state || null,
        pincode: formData?.pincode || null,
        qualification: formData?.qualification || null,
        graduation_year: formData?.graduationYear
          ? parseInt(formData?.graduationYear)
          : null,
        college_name: formData?.collegeName || null,
        technical_skills: formData?.technicalSkills || [],
        has_work_experience: formData?.hasWorkExperience || false,
        experience_years: formData?.experienceYears
          ? parseInt(formData?.experienceYears?.toString()?.split("-")?.[0])
          : null,
        previous_company: formData?.previousCompany || null,
        previous_role: formData?.previousRole || null,
        additional_info: formData?.additionalInfo || null,
        training_mode: formData?.trainingMode || null,
        placement_cities: formData?.placementCities || [],
        terms_accepted: formData?.termsAccepted || false,
        privacy_accepted: formData?.privacyAccepted || false,
        fee_structure_accepted: formData?.feeStructureAccepted || false,
        communication_consent: formData?.communicationConsent || false,
        payment_completed: formData?.paymentCompleted || false,
        payment_id: formData?.paymentId || null,
        order_id: formData?.orderId || null,
        payment_amount: formData?.paymentAmount || null,
      };

      let data, error;

      // FIXED: Use proper insert/update logic instead of upsert
      if (existingRegistration) {
        // Update existing registration
        ({ data, error } = await supabase
          ?.from("registrations")
          ?.update(registrationData)
          ?.eq("id", existingRegistration?.id)
          ?.select()
          ?.single());
      } else {
        // Insert new registration
        ({ data, error } = await supabase
          ?.from("registrations")
          ?.insert(registrationData)
          ?.select()
          ?.single());
      }

      if (error) {
        return { success: false, error: error?.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error?.message };
    }
  },

  // Enhanced get registration that works for both authenticated and guest users
  async getUserRegistration() {
    try {
      const user = await this.getCurrentUser();

      // Enhanced guest user flow
      if (!user) {
        try {
          const guestData = localStorage.getItem("guestRegistrationData");
          if (guestData) {
            const parsedData = JSON.parse(guestData);
            return { success: true, data: parsedData };
          }
          return { success: true, data: null };
        } catch (error) {
          return { success: true, data: null };
        }
      }

      // Authenticated user flow (existing code)
      const { data, error } = await supabase
        ?.from("registrations")
        ?.select("*")
        ?.eq("user_id", user?.id)
        ?.single();

      if (error) {
        if (error?.code === "PGRST116") {
          // No existing registration found
          return { success: true, data: null };
        }
        return { success: false, error: error?.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error?.message };
    }
  },

  // Enhanced guest account creation with better error handling
  async createGuestAccount(formData) {
    try {
      // Check if we already have a verified phone number from guest verification
      const guestPhoneVerified = localStorage.getItem("guestPhoneVerified");
      if (!guestPhoneVerified && formData?.mobileNumber) {
        return {
          success: false,
          error:
            "Please verify your phone number before completing registration",
        };
      }

      // Create account using authService
      const authModule = await import("./authService");
      const authService = authModule?.default;

      if (!authService) {
        return {
          success: false,
          error: "Authentication service not available",
        };
      }

      // Generate a secure temporary password
      const tempPassword = `SecureTemp${formData?.mobileNumber?.slice(
        -4
      )}@${Date.now()}${Math.random()?.toString(36)?.substr(2, 5)}`;

      // Create the user account with proper metadata
      const signUpResult = await authService?.signUp(
        formData?.email,
        tempPassword,
        {
          fullName: formData?.fullName,
          mobileNumber: formData?.mobileNumber,
        }
      );

      let user;
      let session;

      if (
        !signUpResult?.success &&
        signUpResult?.error === "User already registered"
      ) {
        console.log("User already registered. Fetching existing user...");
        // Fetch the user from the profiles table by email
        const { data: existingProfile, error: profileError } = await supabase
          .from("user_profiles")
          .select("*")
          .ilike("email", formData.email)
          .maybeSingle();

        if (profileError) {
          console.warn(
            "Could not fetch existing user profile:",
            profileError.message
          );
          return {
            success: false,
            error:
              "User already registered but unable to fetch profile. Contact support.",
          };
        }
        localStorage.setItem("existingUser", JSON.stringify(existingProfile));
        user = existingProfile;
        session = null; // No session needed
        return { success: true, data: { user, existingProfile: true } };
      } else if (!signUpResult?.success) {
        // Any other signup errors
        return { success: false, error: signUpResult?.error };
      } else {
        // 2. If no session returned, auto sign-in using same credentials
        if (!session && tempPassword) {
          const { data: signInData, error: signInError } =
            await supabase.auth.signInWithPassword({
              email: formData.email,
              password: tempPassword,
            });

          if (signInError) {
            throw signInError;
          }

          user = signInData.user;
          session = signInData.session;
        }

        // Save session token to localStorage
        if (data?.session?.access_token) {
          localStorage.setItem("sol-sb-token", data.session.access_token);
        }

        // ENHANCED: Wait for user creation and profile trigger to complete
        console.log("Waiting for user account and profile creation...");
        await new Promise((resolve) => setTimeout(resolve, 3000));

        // Force refresh auth state to ensure user is immediately available
        let userVerified = false;
        let verifyAttempts = 0;
        const maxVerifyAttempts = 10;

        while (!userVerified && verifyAttempts < maxVerifyAttempts) {
          verifyAttempts++;
          console.log(`Verifying user creation attempt ${verifyAttempts}...`);

          // Try getting the user
          const {
            data: { user: newUser },
            error: getUserError,
          } = await supabase?.auth?.getUser();

          if (newUser && !getUserError) {
            console.log("User account verified successfully");
            userVerified = true;

            // Verify user profile exists
            const { data: profile, error: profileError } = await supabase
              ?.from("user_profiles")
              ?.select("id")
              ?.eq("id", newUser?.id)
              ?.single();

            if (profileError && profileError?.code !== "PGRST116") {
              console.warn("Profile check error:", profileError);
            }

            if (!profile) {
              console.log("Creating user profile...");
              // Create profile if it doesn't exist
              const { error: createProfileError } = await supabase
                ?.from("user_profiles")
                ?.insert({
                  id: newUser?.id,
                  email: newUser?.email,
                  full_name:
                    formData?.fullName ||
                    newUser?.user_metadata?.full_name ||
                    newUser?.email?.split("@")?.[0],
                  mobile_number: formData?.mobileNumber,
                  phone_verified: !!guestPhoneVerified,
                });

              if (
                createProfileError &&
                !createProfileError?.message?.includes("duplicate key")
              ) {
                console.warn("Profile creation error:", createProfileError);
              } else {
                console.log("User profile created successfully");
              }
            }

            break;
          } else {
            console.log(
              `User not yet available, waiting... (${
                getUserError?.message || "No user returned"
              })`
            );
            await new Promise((resolve) => setTimeout(resolve, 1000));
          }
        }

        if (!userVerified) {
          return {
            success: false,
            error:
              "Failed to verify user account creation. Please try logging in with your email and the temporary password, or contact support.",
          };
        }

        // Save the registration data now that user exists
        const saveResult = await this.saveRegistration(formData);
        if (!saveResult?.success) {
          console.warn(
            "Failed to save registration after account creation:",
            saveResult?.error
          );
          // Don't fail the entire process - account was created successfully
        }

        // // Clear guest data from localStorage since it's now in the database
        // localStorage.removeItem("guestRegistrationData");
        // localStorage.removeItem("guestPhoneVerified");

        return {
          success: true,
          data: {
            session: session,
            user: signUpResult?.data?.user,
            registration: saveResult?.data,
            tempPassword: tempPassword,
            message:
              "Account created successfully! Your registration has been saved.",
            instructions:
              "You can now log in with your email and use the temporary password provided.",
          },
        };
      }
    } catch (error) {
      console.error("Guest account creation error:", error);
      return {
        success: false,
        error:
          error?.message || "Failed to create account and save registration",
      };
    }
  },

  // Enhanced submit registration that creates account automatically for guests
  async submitRegistration(formData) {
    try {
      const {
        data: { user },
      } = await supabase?.auth?.getUser();

      // If no user, create guest account first
      if (!user) {
        console.log("Creating guest account for registration submission...");
        const guestAccountResult = await this.createGuestAccount(formData);
        if (!guestAccountResult?.success) {
          return { success: false, error: guestAccountResult?.error };
        }

        // FIXED: Wait longer for complete account setup and force refresh auth state
        await new Promise((resolve) => setTimeout(resolve, 3000));

        // Force refresh the auth state to ensure user is available
        const {
          data: { session },
          error: sessionError,
        } = await supabase?.auth?.refreshSession();
        if (sessionError) {
          console.warn("Session refresh error:", sessionError);
        }

        // Additional wait for auth state propagation
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }

      // Get current user with multiple attempts (handle auth propagation delay)
      let currentUser = null;
      let attempts = 0;
      const maxAttempts = 5;

      while (!currentUser && attempts < maxAttempts) {
        attempts++;
        console.log(`Attempt ${attempts} to get current user...`);

        const {
          data: { user: attemptUser },
        } = await supabase?.auth?.getUser();
        if (attemptUser) {
          currentUser = attemptUser;
          break;
        }

        // Wait before next attempt with exponential backoff
        await new Promise((resolve) => setTimeout(resolve, 1000 * attempts));

        // Try refreshing session on failed attempts
        if (attempts > 2) {
          try {
            const {
              data: { session },
              error,
            } = await supabase?.auth?.refreshSession();
            if (!error && session?.user) {
              currentUser = session?.user;
              break;
            }
          } catch (refreshError) {
            console.warn("Session refresh failed:", refreshError);
          }
        }
      }

      if (!currentUser) {
        // Fallback: Still save to localStorage but notify user
        localStorage.setItem(
          "registrationFormData",
          JSON.stringify({
            ...formData,
            submittedAt: new Date()?.toISOString(),
            status: "submitted",
            pendingAuthentication: true,
          })
        );

        return {
          success: false,
          error:
            "Registration saved locally but authentication failed. Please contact support to complete your registration.",
          code: "AUTH_REQUIRED",
          fallbackData: {
            id: "guest-registration-pending",
            status: "pending_auth",
            message:
              "Registration data saved locally. Authentication will be resolved by support.",
          },
        };
      }

      // FIXED: Ensure registration data is saved before updating status
      const saveResult = await this.saveRegistration(formData);
      if (!saveResult?.success) {
        return {
          success: false,
          error: "Failed to save registration data: " + saveResult?.error,
        };
      }

      // Update status to submitted
      const { data, error } = await supabase
        ?.from("registrations")
        ?.update({
          registration_status: "submitted",
          submitted_at: new Date()?.toISOString(),
        })
        ?.eq("user_id", currentUser?.id)
        ?.select()
        ?.single();

      if (error) {
        return {
          success: false,
          error: "Failed to submit registration: " + error?.message,
        };
      }

      // Clear localStorage data after successful submission
      localStorage.removeItem("registrationFormData");
      localStorage.removeItem("guestRegistrationData");

      return { success: true, data };
    } catch (error) {
      console.error("Registration submission error:", error);
      return {
        success: false,
        error: error?.message || "Failed to submit registration",
      };
    }
  },

  // Upload document (requires authentication)
  async uploadDocument(documentType, file) {
    try {
      const user = await this.getCurrentUser();

      if (!user) {
        return {
          success: false,
          error: "Authentication required for document upload",
        };
      }

      // 1. Fetch the user's registration row
      const { data: registration, error: regError } = await supabase
        .from("registrations")
        .select("id")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (regError || !registration) {
        return {
          success: false,
          error: "No registration record found for this user",
        };
      }

      const registrationId = registration.id;

      // Generate unique filename
      const fileExt = file?.name?.split(".")?.pop();
      const filePath = `${user?.id}/${Date.now()}.${fileExt}`;

      // Upload file to storage
      const { data: uploadData, error: uploadError } = await supabase?.storage
        ?.from("registration-documents")
        ?.upload(filePath, file, {
          contentType: file?.type || "application/pdf",
          upsert: false, // optional, prevents overwriting
        });

      if (uploadError) {
        return { success: false, error: uploadError?.message };
      }

      // Save document metadata to database
      const { data, error } = await supabase
        ?.from("registration_documents")
        ?.insert({
          registration_id: registrationId,
          document_type: documentType,
          file_name: file?.name,
          file_path: uploadData?.path,
          file_size: file?.size,
          mime_type: file?.type,
        })
        ?.select()
        ?.single();

      if (error) {
        return { success: false, error: error?.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error?.message };
    }
  },

  // Get documents for a registration
  async getRegistrationDocuments(registrationId) {
    try {
      const { data, error } = await supabase
        ?.from("registration_documents")
        ?.select("*")
        ?.eq("registration_id", registrationId);

      if (error) {
        return { success: false, error: error?.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error?.message };
    }
  },

  // Get public URL for a document
  async getDocumentUrl(filePath) {
    try {
      const { data } = supabase?.storage
        ?.from("registration-documents")
        ?.getPublicUrl(filePath);

      return { success: true, url: data?.publicUrl };
    } catch (error) {
      return { success: false, error: error?.message };
    }
  },

  // Delete a document
  async deleteDocument(documentId, filePath) {
    try {
      // Delete from storage
      const { error: storageError } = await supabase?.storage
        ?.from("registration-documents")
        ?.remove([filePath]);

      if (storageError) {
        return { success: false, error: storageError?.message };
      }

      // Delete from database
      const { error } = await supabase
        ?.from("registration_documents")
        ?.delete()
        ?.eq("id", documentId);

      if (error) {
        return { success: false, error: error?.message };
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: error?.message };
    }
  },

  // Get all registrations (admin functionality)
  async getAllRegistrations(filters = {}) {
    try {
      let query = supabase?.from("registrations")?.select(`
          *,
          user_profiles:user_id (
            email,
            full_name
          )
        `);

      // Apply filters
      if (filters?.status) {
        query = query?.eq("registration_status", filters?.status);
      }

      if (filters?.profile) {
        query = query?.eq("selected_profile", filters?.profile);
      }

      if (filters?.paymentCompleted !== undefined) {
        query = query?.eq("payment_completed", filters?.paymentCompleted);
      }

      // Order by creation date
      query = query?.order("created_at", { ascending: false });

      const { data, error } = await query;

      if (error) {
        return { success: false, error: error?.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error?.message };
    }
  },

  // Update registration status (admin functionality)
  async updateRegistrationStatus(registrationId, status) {
    try {
      const { data, error } = await supabase
        ?.from("registrations")
        ?.update({ registration_status: status })
        ?.eq("id", registrationId)
        ?.select()
        ?.single();

      if (error) {
        return { success: false, error: error?.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error?.message };
    }
  },
};

export default registrationService;
